import { useEffect, useState } from "react";

/** Recommended threshold for CLS (cumulative layout shift); unitless. */
const CLS_POOR = 0.25;

export interface PerformanceMetrics {
  /** Time from FCP to LCP in ms (delta; more stable in dev than raw LCP). */
  lcpDeltaMs?: number;
  /** Cumulative layout shift. */
  cls?: number;
}

export interface UsePerformanceWarningsResult {
  /** User-facing performance warning strings (empty until run, or when within thresholds). */
  warnings: string[];
  /** True only after the check has completed (or timed out). */
  hasRun: boolean;
  /** Measured values when hasRun is true (for display in passed message). */
  metrics?: PerformanceMetrics;
}

/**
 * Measures FCP→LCP delta and CLS in dev only; returns warnings when above acceptable thresholds.
 * Reports the delta between FCP and LCP (not raw LCP) so dev environment load delay is mostly
 * cancelled out. Runs after load and waits up to a short period for metrics to settle.
 *
 * @returns Object with warnings array and hasRun true only after the check completed.
 */
export function usePerformanceWarnings(): UsePerformanceWarningsResult {
  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasRun, setHasRun] = useState(false);
  const [metrics, setMetrics] = useState<PerformanceMetrics | undefined>(
    undefined,
  );

  useEffect(() => {
    const isDev = process.env.NODE_ENV === "development";
    if (typeof window === "undefined" || !isDev) {
      setWarnings([]);
      setHasRun(false);
      setMetrics(undefined);
      return;
    }

    setHasRun(false);
    setMetrics(undefined);
    let cancelled = false;
    const messages: string[] = [];
    let fcpMs: number | null = null;
    let lcpMs: number | null = null;
    let clsValue = 0;

    const finish = () => {
      if (cancelled) {
        return;
      }
      setWarnings(messages);
      const lcpDeltaMs =
        fcpMs !== null && lcpMs !== null ? lcpMs - fcpMs : undefined;
      setMetrics({
        ...(lcpDeltaMs !== undefined && { lcpDeltaMs }),
        ...(clsValue > 0 && { cls: clsValue }),
      });
      setHasRun(true);
    };

    /** Capture FCP (first contentful paint) for delta with LCP. */
    function observeFCP() {
      try {
        const po = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === "first-contentful-paint") {
              fcpMs = entry.startTime;
              break;
            }
          }
        });
        po.observe({ type: "paint", buffered: true });
        return () => po.disconnect();
      } catch {
        return () => {};
      }
    }

    /** Capture LCP (largest contentful paint); we report FCP→LCP delta, not raw LCP. */
    function observeLCP() {
      try {
        const po = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const last = entries[entries.length - 1];
          if (last) {
            lcpMs = last.startTime;
          }
        });
        po.observe({ type: "largest-contentful-paint", buffered: true });
        return () => po.disconnect();
      } catch {
        return () => {};
      }
    }

    /** Capture CLS via PerformanceObserver (cumulative layout shift). */
    function observeCLS() {
      try {
        const po = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutEntry = entry as PerformanceEntry & {
              hadRecentInput?: boolean;
              value?: number;
            };
            if (
              !layoutEntry.hadRecentInput &&
              typeof layoutEntry.value === "number"
            ) {
              clsValue += layoutEntry.value;
            }
          }
        });
        po.observe({ type: "layout-shift", buffered: true });
        const t = setTimeout(() => {
          if (clsValue > CLS_POOR) {
            messages.push(
              `Performance: CLS ${clsValue.toFixed(2)} exceeds acceptable ${CLS_POOR} (cumulative layout shift).`,
            );
          }
        }, 3000);
        return () => {
          clearTimeout(t);
          po.disconnect();
        };
      } catch {
        return () => {};
      }
    }

    const teardownFCP = observeFCP();
    const teardownLCP = observeLCP();
    const teardownCLS = observeCLS();

    /** Only report from the first ~10s; then finish so we don't report late lazy content. */
    const timeoutId = setTimeout(finish, 10500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      teardownFCP();
      teardownLCP();
      teardownCLS();
    };
  }, []);

  return { warnings, hasRun, metrics };
}
